import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';

interface ContactFormData {
  name: string;
  email: string;
  company?: string;
  phone?: string;
  subject?: string;
  message: string;
}

export const ContactFormHandler = ({ 
  children, 
  onSubmitSuccess 
}: { 
  children: (props: {
    formData: ContactFormData;
    setFormData: (data: ContactFormData) => void;
    handleSubmit: (e: React.FormEvent) => void;
    loading: boolean;
    isSubmitted: boolean;
  }) => React.ReactNode;
  onSubmitSuccess?: () => void;
}) => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setLoading(true);

    try {
      // Insert enquiry into database
      const { data: enquiryData, error } = await supabase
        .from('enquiries')
        .insert({
          name: formData.name,
          email: formData.email,
          company: formData.company || null,
          phone: formData.phone || null,
          subject: formData.subject || null,
          message: formData.message,
        })
        .select()
        .single();

      if (error) {
        throw error;
      }

      // Send email notifications in background
      supabase.functions.invoke('send-form-notification', {
        body: {
          enquiryId: enquiryData.id,
          name: formData.name,
          email: formData.email,
          company: formData.company,
          phone: formData.phone,
          subject: formData.subject,
          message: formData.message,
          formType: 'Contact Form'
        }
      }).catch(error => {
        console.error('Error sending email notification:', error);
      });

      // Sync to Hubspot in background
      supabase.functions.invoke('sync-to-hubspot', {
        body: {
          enquiryId: enquiryData.id,
          name: formData.name,
          email: formData.email,
          company: formData.company,
          phone: formData.phone,
          subject: formData.subject,
          message: formData.message,
          formType: 'Contact Form'
        }
      }).catch(error => {
        console.error('Error syncing to Hubspot:', error);
      });

      // Show success message
      toast({
        title: "Submission Successful!",
        description: "We've received your message and will be in touch shortly.",
        duration: 5000,
      });

      // Set submitted state to show thank you message
      setIsSubmitted(true);

      onSubmitSuccess?.();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'Failed to send message. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {children({ formData, setFormData, handleSubmit, loading, isSubmitted })}
    </>
  );
};